import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { postToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import handleApolloError from '../../utils/error/handleApolloError';

const THREAD_FRAGMENT = gql(`
  fragment useUnresolveThread_thread on RootThreadFields {
    id
  }
`);

const UNRESOLVE_THREAD_MUTATION = gql(/** @complexity 2 */ `
  mutation unresolveThread($id: ID!) {
    unresolveThread(threadId: $id) {
      id
    }
  }
`);

const useUnresolveThread = () => {
  const dispatch = useAppDispatch();
  const [mutation] = useMutation(UNRESOLVE_THREAD_MUTATION);

  return useCallback(
    async (thread: DocumentType<typeof THREAD_FRAGMENT>) => {
      try {
        return await mutation({
          variables: { id: thread.id },
          optimisticResponse: { unresolveThread: { id: thread.id } },
          update: (cache) => {
            cache.modify({
              id: cache.identify(thread),
              fields: {
                resolvedAt: () => null,
              },
            });
          },
        });
      } catch (e) {
        handleApolloError(e, 'VALIDATION', () =>
          dispatch(UIActions.pushToast(postToasts.threadUnresolveError)),
        );
        throw e;
      }
    },
    [mutation, dispatch],
  );
};

export default useUnresolveThread;
